import { createRef, useContext, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { AppContext } from "../context/AppContext";
import { deleteUser, getDepartments, getSuggestions, getUserById, logoutUser, updateBillingDetails, updatePermissions, updateSetting, updateUser } from "../services/adminServices";
import { CONFIG } from "../config/general";
import SimpleReactValidator from "simple-react-validator";
import { toast } from "react-toastify";
import { Cropper } from "react-cropper";
import DropdownWithCheckbox from "./Selection";
import socket from "../services/socketConnection";
import RemoteBtn from "./RemoteBtn.js";

const ViewProfile = () => {
  const { id } = useParams();
  const { login } = useContext(AppContext);
  const [validatorCount, setValidatorCount] = useState(0);
  const forceUpdate = () => setValidatorCount(validatorCount + 1);
  const initial = new SimpleReactValidator({
    locale: "en", autoForceUpdate: { forceUpdate }, validators: {
      passwordValidation: {
        message: "Password must be at least 8 characters long and include at least one uppercase letter, one lowercase letter, one number, and one special character.",
        rule: (val, params, validator) => {
          const regex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&#])[A-Za-z\d@$!%*?&#]{8,}$/;
          return regex.test(val);
        },
        required: true,
      },
    },
  });
  const [validator, setValidator] = useState(initial);
  const [activeTab, setActiveTab] = useState("Edit");
  const [departments, setDepartments] = useState([]);
  const [setting, setSetting] = useState(null);
  const [suggestion, setSuggestion] = useState([]);
  const cropperRef = createRef();
  const [systemInfo, setSystemInfo] = useState({});
  const [isBilling, setIsBilling] = useState({
    _id: null,
    selection: false,
    includeArray: [],
    includeValue: "",
    notIncludeValue: "",
    notIncludeArray: [],
    unAccessableApps: []
  });
  const [user, setUser] = useState(null);
  const [editMember, setEditMember] = useState(null);

  const handleDeleteUser = async (id) => {
    try {
      let result = await deleteUser(login.token, id)
      if (result.success) {
        toast.success(result.message)
      } else {
        toast.error(result.message)
      }
      getUserData()
    } catch (error) {
      console.log(error)
    }
  }

  const handleLogout = async (id) => {
    try {
      let result = await logoutUser(login.token, id)
      socket.emit("admin-user-logout", { userId: id });
      if (result.success) {
        toast.success(result.message)
      } else {
        toast.error(result.message)
      }
      getUserData()
    } catch (error) {
      console.log(error)
    }
  }

  const getUserData = async () => {
    try {
      let result = await getUserById(login.token, id);
      setUser(result.data);
      setEditMember(result.data)
      setSystemInfo(result?.data?.systemInfo || {})
      setPermissions(result?.data?.permissions || [])
    } catch (error) {
      console.error("Error fetching users:", error);
    }
  }

  const handleUpdateBilling = async () => {
    if (!isBilling?._id) {
      toast.error("Something went wrong")
      return;
    }
    const payload = {
      includeArray: isBilling.includeArray || [],
      notIncludeArray: isBilling.notIncludeArray || [],
      unAccessableApps: isBilling.unAccessableApps || [],
    };
    let result = await updateBillingDetails(login.token, isBilling._id, payload);
    if (result.success) {
      toast.success(result.message)
    } else {
      toast.error(result.message)
    }
    getUserData()
    setIsBilling({
      _id: null,
      includeArray: [],
      includeValue: "",
      notIncludeValue: "",
      notIncludeArray: []
    })
  }

  const handleFileChange = (e) => {
    let files;
    if (e.dataTransfer) {
      files = e.dataTransfer.files;
    } else if (e.target) {
      files = e.target.files;
    }
    const reader = new FileReader();
    reader.onload = () => {
      setEditMember({ ...editMember, isImgUpdated: true, profileImage: reader.result })
    };
    reader.readAsDataURL(files[0]);
  };

  const onCrop = () => {
    const cropper = cropperRef.current?.cropper;
    if (cropper) {
      const cropBoxData = cropper.getCropBoxData();
      const cropWidth = cropBoxData.width;
      const cropHeight = cropBoxData.height;
      if (cropWidth < 100 || cropHeight < 100) {
        let newCropWidth = Math.max(cropWidth, 100);
        let newCropHeight = Math.max(cropHeight, 100);
        cropper.setCropBoxData({ width: newCropWidth, height: newCropHeight });
      }
    }
  };

  const handleUpdateMember = async () => {
    try {
      if (validator.allValid()) {
        const formData = new FormData();
        const cropper = cropperRef.current?.cropper;
        let memberDetails = { ...editMember };
        delete memberDetails.profileImage;
        let isPassword =
          editMember.userPassword == user.userPassword;
        if (isPassword) {
          delete memberDetails.userPassword;
        }
        Object.entries(memberDetails).forEach(([key, value]) => {
          formData.append(key, JSON.stringify(value));
        });

        if (editMember.isImgUpdated && cropper) {
          // Process and upload the new profile picture
          cropper.getCroppedCanvas().toBlob(async (blob) => {
            if (blob) {
              const croppedFile = new File([blob], "profile-pic.png", { type: "image/png" });
              formData.append("updatedPic", croppedFile);
            }
            // Send updated data to the server
            const result = await updateUser(login.token, editMember._id, formData);
            if (result.success) {
              toast.success(result.message)
            } else {
              toast.error(result.message)
            }
            setEditMember(null);
            getUserData();
          });
        } else {
          const result = await updateUser(login.token, editMember._id, formData);
          if (result.success) {
            toast.success(result.message)
          } else {
            toast.error(result.message)
          }
          setEditMember(null);
          getUserData();
        }
      } else {
        validator.showMessages();
        forceUpdate();
      }
    } catch (error) {
      console.error("Error updating member:", error);
      toast.error("Something went wrong")
    }
  };

  const getAllDepartments = async () => {
    try {
      let result = await getDepartments(login.token, {});
      setDepartments(result.data)
    } catch (error) {
      console.error("Error fetching users:", error);
    }
  };

  const getAllSuggestions = async () => {
    try {
      let result = await getSuggestions(login.token);
      setSuggestion(result.suggestions)
    } catch (error) {
      console.error("Error fetching users:", error);
    }
  };

  const handleUpdateSettings = async () => {
    try {
      const result = await updateSetting(login.token, id, setting);
      if (result.success) {
        toast.success(result.message)
      } else {
        toast.error(result.message)
      }
    } catch (error) {
      console.error("Error fetching users:", error);
    }
  }
  const [permissions, setPermissions] = useState([])
  const handlePermissionChange = (permissionKey, isChecked) => {
    if (isChecked) {
      setPermissions((prev) => [...new Set([...prev, permissionKey])]);
    } else {
      setPermissions((prev) => prev.filter((perm) => perm !== permissionKey));
    }
  };

  const handleApplyPermissions = async () => {
    const payload = {
      permissions: permissions,
      userId: id
    }
    if (login.user.userEmail == user.userEmail) {
      toast.error("You can't update your own permissions")
      return;
    }
    try {
      const result = await updatePermissions(login.token, payload);
      if (result.success) {
        toast.success(result.message)
      } else {
        toast.error(result.message)
      }
    } catch (error) {
      console.error("Error fetching users:", error);
    }
  };



  useEffect(() => {
    getUserData()
    getAllDepartments();
    getAllSuggestions()
  }, [id]);

  if (!user) return <p>Loading profile...</p>;

  return (
    <div className="details-table mt-3">
      <div className="d-flex justify-content-around">
        <div className="d-flex flex-column justify-content-center">
          <img
            src={CONFIG.REACT_APP_UPLOADS + "/" + user.profileImage}
            alt={user.name}
            className="mx-auto"
            style={{ "width": "100px", "borderRadius": "50%" }}
          />
          <h2 className="text-xl font-semibold text-center mt-4">{user.name}</h2>
          <p className="text-gray-500 text-center">{user.designation}</p>
        </div>
        <div className="d-flex gap-2">
          <div className="export-chat edit-delete-user">
            <a onClick={() => handleDeleteUser(user._id)}>
              <i className="fa-regular fa-trash-can"></i>{user.isDeleted ? "Enable" : "Disable"}
            </a>
          </div>
          <div className="export-chat edit-delete-user">
            <RemoteBtn userId={user._id} />
          </div>
          <div className="export-chat edit-delete-user">
            <a onClick={() => handleLogout(user._id)}>
              <i class="fa fa-sign-out" aria-hidden="true"></i>Logout</a>
          </div>
        </div>

      </div>
      <ul className="nav nav-tabs justify-content-center mb-3">
        <li className="nav-item">
          <a className={`nav-link ${activeTab === "Edit" ? "active" : ""}`} data-bs-toggle="tab" onClick={() => { setActiveTab("Edit"); setEditMember(user) }}>Edit</a>
        </li>
        <li className="nav-item">
          <a className={`nav-link ${activeTab === "Work" ? "active" : ""}`} data-bs-toggle="tab" onClick={() => {
            setActiveTab("Work");
            setIsBilling({
              ...isBilling,
              selection: user.billingDetails?.notIncludeArray.length > 0 ? false : true,
              notIncludeArray: user.billingDetails?.notIncludeArray || [],
              includeArray: user.billingDetails?.includeArray || [],
              unAccessableApps: user.billingDetails?.unAccessableApps || [],
              _id: user._id
            })
          }}>Edit Work Details</a>
        </li>
        <li className="nav-item">
          <a className={`nav-link ${activeTab === "Settings" ? "active" : ""}`} data-bs-toggle="tab" onClick={() => { setActiveTab("Settings"); setSetting(user.settings || {}) }}>Settings</a>
        </li>
        <li className="nav-item">
          <a className={`nav-link ${activeTab === "system" ? "active" : ""}`} data-bs-toggle="tab" onClick={() => { setActiveTab("system"); }}>System Info</a>
        </li>
        <li className="nav-item">
          <a className={`nav-link ${activeTab === "permissions" ? "active" : ""}`} data-bs-toggle="tab" onClick={() => { setActiveTab("permissions"); }}>Permissions</a>
        </li>
      </ul>
      {(isBilling?._id && activeTab === "Work") && (
        <div className="mt-4">

          {/* Dropdown to select Include / Not Include */}
          <div className="mb-3">
            <div className="dropdown">
              <button
                className="btn btn-sm btn-outline-secondary dropdown-toggle w-100 text-start"
                type="button"
                id="billingSelection"
                data-bs-toggle="dropdown"
                aria-expanded="false"
              >
                {isBilling.selection ? "What to Include" : "What not to Include"}
              </button>

              <ul className="dropdown-menu w-100" aria-labelledby="billingSelection">
                <li>
                  <button
                    className="dropdown-item"
                    onClick={() =>
                      setIsBilling((prev) => ({
                        ...prev,
                        selection: false,
                        includeArray: [],
                        notIncludeArray: [],
                        includeValue: "",
                        notIncludeValue: "",
                      }))
                    }
                  >
                    What not to Include
                  </button>
                </li>
                <li>
                  <button
                    className="dropdown-item"
                    onClick={() =>
                      setIsBilling((prev) => ({
                        ...prev,
                        selection: true,
                        includeArray: [],
                        notIncludeArray: [],
                        includeValue: "",
                        notIncludeValue: "",
                      }))
                    }
                  >
                    What to Include
                  </button>
                </li>
              </ul>
            </div>
          </div>

          {/* Input + Badges */}
          <div className="mb-3">
            <label className="form-label">
              {isBilling.selection ? "What to Include" : "What not to Include"}
            </label>
            <div className="d-flex gap-2 flex-wrap align-items-center mb-2">
              <input
                type="text"
                value={isBilling.selection ? isBilling.includeValue : isBilling.notIncludeValue || ""}
                onChange={(e) =>
                  setIsBilling((prev) => ({
                    ...prev,
                    [isBilling.selection ? "includeValue" : "notIncludeValue"]: e.target.value,
                  }))
                }
                className="form-control me-2"
                placeholder="Enter a value"
                style={{ flex: "1 1 auto", minWidth: "150px" }}
              />
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => {
                  const key = isBilling.selection ? "includeArray" : "notIncludeArray";
                  const valueKey = isBilling.selection ? "includeValue" : "notIncludeValue";
                  if (isBilling[valueKey].trim()) {
                    setIsBilling((prev) => ({
                      ...prev,
                      [key]: [...prev[key], prev[valueKey]],
                      [valueKey]: "",
                    }));
                  }
                }}
              >
                Add
              </button>
            </div>

            {/* Display badges instead of list */}
            <div className="d-flex flex-wrap gap-2">
              {(isBilling.selection ? isBilling.includeArray : isBilling.notIncludeArray).map((item, index) => (
                <span
                  key={index}
                  className="badge bg-primary d-flex align-items-center"
                  style={{ padding: "6px 10px", borderRadius: "20px" }}
                >
                  {item}
                  <i
                    className="fa fa-times ms-2"
                    style={{ cursor: "pointer" }}
                    onClick={() => {
                      const key = isBilling.selection ? "includeArray" : "notIncludeArray";
                      setIsBilling((prev) => ({
                        ...prev,
                        [key]: prev[key].filter((_, i) => i !== index),
                      }));
                    }}
                  ></i>
                </span>
              ))}
            </div>
          </div>

          {/* Suggested Items */}
          <div className="d-flex gap-2 flex-wrap mt-2">
            {suggestion.map((input, index) =>
              ![...isBilling.notIncludeArray, ...isBilling.includeArray].includes(input) && (
                <span
                  key={index}
                  className="badge bg-secondary align-items-center"
                  style={{ padding: "6px 10px", borderRadius: "20px", cursor: "pointer" }}
                  onClick={() => {
                    const key = isBilling.selection ? "includeArray" : "notIncludeArray";
                    setIsBilling((prev) => ({
                      ...prev,
                      [key]: [...prev[key], input],
                    }));
                  }}
                >
                  {input} <i className="fa fa-plus-circle ms-2"></i>
                </span>
              )
            )}
          </div>

          <div className="col-lg-3">
            <label className="form-label">Unaccessible Apps</label>

            <div className="d-flex flex-wrap gap-2 mb-2">
              {isBilling.unAccessableApps?.map((app, idx) => (
                <span key={idx} className="badge bg-danger p-2 d-flex align-items-center">
                  {app}
                  <button
                    type="button"
                    className="btn-close btn-close-white ms-2"
                    style={{ fontSize: "10px" }}
                    onClick={() => {
                      const updated = isBilling.unAccessableApps.filter((_, i) => i !== idx);
                      setIsBilling({ ...isBilling, unAccessableApps: updated });
                    }}
                  ></button>
                </span>
              ))}
            </div>

            <input
              type="text"
              className="form-control"
              placeholder="Press Enter to add app"
              onKeyDown={(e) => {
                if (e.key === "Enter" && e.target.value.trim()) {
                  e.preventDefault();
                  setIsBilling({
                    ...isBilling,
                    unAccessableApps: [
                      ...(isBilling.unAccessableApps || []),
                      e.target.value.trim()
                    ],
                  });
                  e.target.value = "";
                }
              }}
            />
          </div>

          {/* Save Button */}
          <div className="text-end mt-3">
            <button type="button" className="btn btn-success" onClick={handleUpdateBilling}>
              Save
            </button>
          </div>
        </div>
      )}


      {editMember && activeTab === "Edit" && (
        <div className="container mt-4">
          <form>
            <div className="row g-3">
              {/* ===== Column 1 ===== */}
              <div className="col-lg-4">
                {/* Name */}
                <div className="mb-3">
                  <label className="form-label">Name</label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Enter your name"
                    value={editMember.name}
                    onChange={(e) =>
                      setEditMember({ ...editMember, name: e.target.value })
                    }
                  />
                  <span className="text-danger small">
                    {validator.message(
                      "Name",
                      editMember.name,
                      "required|max:50",
                      { max: "Name cannot exceed 50 characters." }
                    )}
                  </span>
                </div>

                {/* Designation */}
                <div className="mb-3">
                  <label className="form-label">Designation</label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Enter your designation"
                    value={editMember.designation}
                    onChange={(e) =>
                      setEditMember({ ...editMember, designation: e.target.value })
                    }
                  />
                  <span className="text-danger small">
                    {validator.message(
                      "Designation",
                      editMember.designation,
                      "required|max:50",
                      { max: "Designation cannot exceed 50 characters." }
                    )}
                  </span>
                </div>

                {/* Mobile */}
                <div className="mb-3">
                  <label className="form-label">Mobile Number</label>
                  <input
                    type="number"
                    className="form-control"
                    placeholder="Enter your mobile number"
                    value={editMember.userPhone}
                    onChange={(e) => {
                      if (e.target.value.length > 10 || isNaN(e.target.value)) return;
                      setEditMember({ ...editMember, userPhone: e.target.value });
                    }}
                  />
                  <span className="text-danger small">
                    {validator.message(
                      "Phone Number",
                      editMember.userPhone,
                      "required|regex:^\\d{10}$",
                      { regex: "Enter a valid 10-digit phone number." }
                    )}
                  </span>
                </div>

                <div className="row mt-3">
                  <div className="col-lg-12 d-flex align-items-center">
                    <label className="form-label me-3">Update Password:</label>
                    <div className="form-check form-switch">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        checked={editMember.isPasswordUpdate}
                        onChange={(e) =>
                          setEditMember({
                            ...editMember,
                            isPasswordUpdate: e.target.checked,
                            userPassword: e.target.checked ? "" : editMember.userPassword,
                          })
                        }
                      />
                    </div>
                  </div>

                  {editMember.isPasswordUpdate && (
                    <div className="col-lg-12 mt-2">
                      <label className="form-label">Password</label>
                      <input
                        type="password"
                        className="form-control"
                        placeholder="Enter new password"
                        value={editMember.userPassword}
                        onChange={(e) =>
                          setEditMember({
                            ...editMember,
                            userPassword: e.target.value,
                          })
                        }
                      />
                      <span className="text-danger small">
                        {validator.message(
                          "password",
                          editMember.userPassword,
                          "required|passwordValidation"
                        )}
                      </span>
                    </div>
                  )}
                </div>
              </div>

              {/* ===== Column 2 ===== */}
              <div className="col-lg-4">
                {/* Username */}
                <div className="mb-3">
                  <label className="form-label">Username</label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Enter your username"
                    value={editMember.username}
                    onChange={(e) =>
                      setEditMember({ ...editMember, username: e.target.value })
                    }
                  />
                  <span className="text-danger small">
                    {validator.message(
                      "username",
                      editMember.username,
                      "required|max:50",
                      { max: "username cannot exceed 50 characters." }
                    )}
                  </span>
                </div>

                {/* Email */}
                <div className="mb-3">
                  <label className="form-label">Email</label>
                  <input
                    type="email"
                    className="form-control"
                    placeholder="Enter your email"
                    value={editMember.userEmail}
                    onChange={(e) =>
                      setEditMember({ ...editMember, userEmail: e.target.value })
                    }
                  />
                  <span className="text-danger small">
                    {validator.message(
                      "userEmail",
                      editMember.userEmail,
                      "required|email|max:50",
                      { max: "Email cannot exceed 50 characters." }
                    )}
                  </span>
                </div>

                {/* Gender */}
                <div className="mb-3">
                  <label className="form-label">Gender</label>
                  <div className="dropdown">
                    <button
                      className="btn btn-sm btn-outline-secondary dropdown-toggle w-100"
                      type="button"
                      id="editMemberGender"
                      data-bs-toggle="dropdown"
                      aria-expanded="false"
                    >
                      {editMember.gender
                        ? editMember.gender.charAt(0).toUpperCase() +
                        editMember.gender.slice(1)
                        : "Select Gender"}
                    </button>
                    <ul
                      className="dropdown-menu w-100"
                      aria-labelledby="editMemberGender"
                    >
                      <li>
                        <button
                          className="dropdown-item"
                          type="button"
                          onClick={() => setEditMember({ ...editMember, gender: "" })}
                        >
                          Select Gender
                        </button>
                      </li>
                      <li>
                        <button
                          className="dropdown-item"
                          type="button"
                          onClick={() =>
                            setEditMember({ ...editMember, gender: "male" })
                          }
                        >
                          Male
                        </button>
                      </li>
                      <li>
                        <button
                          className="dropdown-item"
                          type="button"
                          onClick={() =>
                            setEditMember({ ...editMember, gender: "female" })
                          }
                        >
                          Female
                        </button>
                      </li>
                    </ul>
                  </div>
                  <span className="text-danger small">
                    {validator.message("gender", editMember.gender, "required")}
                  </span>
                </div>

                {/* Departments */}
                <div className="mb-3">
                  <label className="form-label">Select Departments</label>
                  <DropdownWithCheckbox
                    options={departments}
                    selectedItems={editMember?.departments || []}
                    setSelectedItems={(data) => {
                      setEditMember({ ...editMember, departments: data });
                    }}
                  />
                  <span className="text-danger small">
                    {validator.message(
                      "departments",
                      editMember.departments,
                      "required"
                    )}
                  </span>
                </div>
              </div>

              {/* ===== Column 3 ===== */}
              <div className="col-lg-4 text-center">
                {/* Profile Image Upload */}
                <div className="mb-3">
                  <label htmlFor="profileImage" className="form-label">
                    Profile Image
                  </label>
                  <input
                    type="file"
                    className="form-control"
                    id="profileImage"
                    accept="image/*"
                    onChange={(e) => {
                      handleFileChange(e, true);
                    }}
                  />
                  <span className="text-danger small">
                    {validator.message(
                      "Profile Image",
                      editMember.profileImage,
                      "required"
                    )}
                  </span>
                </div>

                {/* Show Preview or Cropper */}
                <div style={{ width: "100%", height: "200px" }}>
                  {editMember.isImgUpdated ? (
                    <Cropper
                      ref={cropperRef}
                      src={
                        editMember.isImgUpdated
                          ? editMember.profileImage
                          : CONFIG.REACT_APP_UPLOADS + "/" + editMember.profileImage
                      }
                      aspectRatio={1}
                      style={{ width: "100%", height: "100%" }}
                      initialAspectRatio={1}
                      viewMode={1}
                      crop={onCrop}
                      minCropBoxWidth={100}
                      minCropBoxHeight={100}
                      background={false}
                      responsive={true}
                      autoCropArea={1}
                      checkOrientation={false}
                      guides={true}
                    />
                  ) : (
                    <img
                      src={CONFIG.REACT_APP_UPLOADS + "/" + editMember.profileImage}
                      alt="Profile Preview"
                      className="img-fluid rounded shadow"
                      style={{ maxHeight: "200px", objectFit: "cover" }}
                    />
                  )}
                </div>
              </div>
            </div>
          </form>

          {/* Save Button */}
          <div className="text-end mt-3">
            <button className="btn btn-success" onClick={handleUpdateMember}>
              Save
            </button>
          </div>
        </div>
      )}


      {activeTab === "Settings" && (
        <div className="container mt-4">
          <div className="mb-3 flex items-center gap-2">
            <div className="mb-3 d-flex gap-3">
              <input
                type="checkbox"
                checked={setting.timetrackingPage}
                onChange={(e) => {
                  setSetting({ ...setting, timetrackingPage: e.target.checked });
                }}
                className="form-checkbox h-5 w-5 text-blue-600"
              />
              <label className="text-gray-700">Time Tracking Page</label>
            </div>
            <div className="mb-3 d-flex gap-3">
              <input
                type="checkbox"
                checked={setting.manualEntry}
                onChange={(e) => {
                  setSetting({ ...setting, manualEntry: e.target.checked });
                }}
                className="form-checkbox h-5 w-5 text-blue-600"
              />
              <label className="text-gray-700">Manual Entry</label>
            </div>
            <div className="mb-3 d-flex gap-3">
              <input
                type="checkbox"
                checked={setting.totalDuration}
                onChange={(e) => {
                  setSetting({ ...setting, totalDuration: e.target.checked });
                }}
                className="form-checkbox h-5 w-5 text-blue-600"
              />
              <label className="text-gray-700">Total Duration</label>
            </div>
            <div className="mb-3 d-flex gap-3">
              <input
                type="checkbox"
                checked={setting.deleteEntry}
                onChange={(e) => {
                  setSetting({ ...setting, deleteEntry: e.target.checked });
                }}
                className="form-checkbox h-5 w-5 text-blue-600"
              />
              <label className="text-gray-700">Delete Entry</label>
            </div>
            <div className="mb-3 d-flex gap-3 align-items-center">
              <input
                type="checkbox"
                checked={setting.screenshotTimer > 0}
                onChange={(e) => {
                  setSetting({
                    ...setting,
                    screenshotTimer: e.target.checked ? 10 : 0, // default to 10 minutes if enabled
                  });
                }}
                className="form-checkbox h-5 w-5 text-blue-600"
              />
              <label className="text-gray-700">Enable Screenshot</label>

              {setting.screenshotTimer > 0 && (
                <input
                  type="number"
                  min={1}
                  className="form-control w-auto"
                  value={setting.screenshotTimer}
                  onChange={(e) =>
                    setSetting({
                      ...setting,
                      screenshotTimer: Math.max(1, parseInt(e.target.value || '1')),
                    })
                  }
                />
              )}
              {setting.screenshotTimer > 0 && <span className="text-muted">minutes</span>}
            </div>
            <div className="mb-3 d-flex gap-3 align-items-center">
              <label className="text-gray-700">Remote Password</label>
              <input
                value={setting.remotePass}
                onChange={(e) => {
                  setSetting({ ...setting, remotePass: e.target.value });
                }}
                placeholder="Password"
                style={{ width: "20%" }}
                className="form-control"
              />
              <span className="text-danger small">
                {validator.message(
                  "remote password",
                  setting.remotePass,
                  "required|min:6"
                )}
              </span>
            </div>
          </div>
          <div className="text-end mt-3">
            <button className="btn btn-success" onClick={handleUpdateSettings}>
              Save
            </button>
          </div>
        </div>
      )}
      {activeTab === "system" && systemInfo && (
        <div className="container mt-4">
          <div className="row mb-4">
            <div className="col-md-6">
              <h5>System</h5>
              <p><strong>Manufacturer:</strong> {systemInfo.systemManufacturer}</p>
              <p><strong>Model:</strong> {systemInfo.systemModel}</p>
              <p><strong>Serial:</strong> {systemInfo.systemSerial}</p>
            </div>

            <div className="col-md-6">
              <h5>Motherboard</h5>
              <p><strong>Manufacturer:</strong> {systemInfo.motherboardManufacturer}</p>
              <p><strong>Model:</strong> {systemInfo.motherboardModel}</p>
              <p><strong>Serial:</strong> {systemInfo.motherboardSerial}</p>
            </div>
          </div>

          <div className="row mb-4">
            <div className="col-md-6">
              <h5>BIOS</h5>
              <p><strong>Version:</strong> {systemInfo.biosVersion}</p>
              <p><strong>Serial:</strong> {systemInfo.biosSerial}</p>
            </div>

            <div className="col-md-6">
              <h5>CPU</h5>
              <p><strong>Brand:</strong> {systemInfo.cpuBrand}</p>
              <p><strong>Vendor:</strong> {systemInfo.cpuVendor}</p>
              <p><strong>ID:</strong> {systemInfo.cpuId}</p>
            </div>
          </div>

          <div className="row mb-4">
            <div className="col-md-6">
              <h5>Operating System</h5>
              <p><strong>Platform:</strong> {systemInfo.os?.platform}</p>
              <p><strong>Distro:</strong> {systemInfo.os?.distro}</p>
              <p><strong>Release:</strong> {systemInfo.os?.release}</p>
              <p><strong>Architecture:</strong> {systemInfo.os?.arch}</p>
            </div>
          </div>

          <div className="row mb-4">
            <div className="col-12">
              <h5>Disks</h5>
              {systemInfo.diskSerials?.length > 0 ? (
                <div className="table-responsive">
                  <table className="table-hover table-bordered align-middle">
                    <thead>
                      <tr>
                        <th>Name</th>
                        <th>Type</th>
                        <th>Serial</th>
                        <th>Size (bytes)</th>
                      </tr>
                    </thead>
                    <tbody>
                      {systemInfo.diskSerials.map((disk, index) => (
                        <tr key={index}>
                          <td>{disk.name}</td>
                          <td>{disk.type}</td>
                          <td>{disk.serial}</td>
                          <td>{disk.size}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : <p>No disks found.</p>}
            </div>
          </div>

          <div className="row mb-4">
            <div className="col-12">
              <h5>Network Interfaces</h5>
              {systemInfo.networkInterfaces?.length > 0 ? (
                <div className="table-responsive">
                  <table className="table-hover table-bordered align-middle">
                    <thead>
                      <tr>
                        <th>Interface</th>
                        <th>IP (v4)</th>
                        <th>MAC Address</th>
                        <th>Internal</th>
                      </tr>
                    </thead>
                    <tbody>
                      {systemInfo.networkInterfaces.map((net, index) => (
                        <tr key={index}>
                          <td>{net.iface}</td>
                          <td>{net.ip4}</td>
                          <td>{net.mac}</td>
                          <td>{net.internal ? "Yes" : "No"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : <p>No network interfaces found.</p>}
            </div>
          </div>

          <div className="row mb-4">
            <div className="col-12">
              <h5>Apps</h5>
              {systemInfo.apps?.length > 0 ? (
                <div className="table-responsive">
                  <table className="table-hover table-bordered align-middle">
                    <thead>
                      <tr>
                        <th>Name</th>
                        <th>Version</th>
                      </tr>
                    </thead>
                    <tbody>
                      {systemInfo.apps.map((app, index) => (
                        <tr key={index}>
                          <td>{app.name}</td>
                          <td>{app.version}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : <p>No apps found.</p>}
            </div>
          </div>
        </div>
      )}
      {
        activeTab === "permissions" && (
          <div className="container mt-4">

            <div className="permissions-list">
              <div className="form-check">
                <input
                  type="checkbox"
                  className="form-check-input"
                  id="chatViewRead"
                  checked={permissions.includes("chat:view")}
                  onChange={(e) => handlePermissionChange("chat:view", e.target.checked)}
                />
                <label className="form-check-label" htmlFor="chatViewRead">
                  Chat - View
                </label>
              </div>

              <div className="form-check">
                <input
                  type="checkbox"
                  className="form-check-input"
                  id="timeTrackingRead"
                  checked={permissions.includes("timetracking:read")}
                  onChange={(e) => handlePermissionChange("timetracking:read", e.target.checked)}
                />
                <label className="form-check-label" htmlFor="timeTrackingRead">
                  Time Tracking - Read
                </label>
              </div>

              <div className="form-check">
                <input
                  type="checkbox"
                  className="form-check-input"
                  id="userGroupDeptCU"
                  checked={permissions.includes("usergroup:create_update")}
                  onChange={(e) => handlePermissionChange("usergroup:create_update", e.target.checked)}
                />
                <label className="form-check-label" htmlFor="userGroupDeptCU">
                  User & Group & Department - Create/Update
                </label>
              </div>

              <div className="form-check">
                <input
                  type="checkbox"
                  className="form-check-input"
                  id="screenshotRead"
                  checked={permissions.includes("screenshot:read")}
                  onChange={(e) => handlePermissionChange("screenshot:read", e.target.checked)}
                />
                <label className="form-check-label" htmlFor="screenshotRead">
                  Screenshot - Read
                </label>
              </div>
            </div>

            {/* Apply Button */}
            <div className="mt-4">
              <button className="btn btn-primary" onClick={handleApplyPermissions}>
                Apply
              </button>
            </div>
          </div>
        )
      }


    </div >
  );
};

export default ViewProfile;
